/*
 * @providesModule SyncMatchMakingController
 *
 */

import React from 'react';

import _ from 'lodash';
import {_m} from 'INTL';
import AWSManager from 'AWSManager';
import CheckoutFlowModalController from 'CheckoutFlowModalController';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import GlobalEventEmitter from 'GlobalEventEmitter';
import LoadingIndicator from 'LoadingIndicator';
import Match from 'SKZMatch';
import MatchJoinController, {JOIN_ERRORS} from 'MatchJoinController';
import {MatchmakingConstants} from 'MatchmakingConstants';
import MatchSelectController from 'MatchSelectController';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import NetClient from 'NetClient';
import NetworkConnectivityClient from 'NetworkConnectivityClient';
import Prefs from 'Preferences';
import SKZConstants from 'SKZConstants';
import SKZTournamentTemplateSet from 'SKZTournamentTemplateSet';
import StakesSelectorController from 'StakesSelectorController';
import StaticWarningModal from 'StaticWarningModal';
import SyncFTUEController from 'SyncFTUEController';
import SyncUtils from 'SyncUtils';
import Tournament from 'SKZTournament';
import TournamentPendingModal from 'TournamentPendingModal';
import constants from 'HomeScreenConstants';

import {AppState, NativeModules, Platform} from 'react-native';

const SkillzModule = NativeModules.SkillzModule;
const CURRENTLY_MATCHMAKING_TOURNAMENT = 'currentlyMatchmakingTournament';
const CURRENTLY_MATCHMAKING_USER = 'currentlyMatchmakingUser';
const IS_CURRENTLY_MATCHMAKING = 'isCurrentlyMatchmaking';
const MATCH_URL = 'matchmaker/matches';
const MAX_RETRIES = 3;
const MILLIS_PER_SECOND = SKZConstants.MS_PER_SEC;
const MODAL_GRACE_PERIOD = 5000; /* for preventing modal spam */
const NOT_FOUND = 404;

class SyncMatchMakingController {
  // the current update handler
  updateHandler: (error: Object, response: Object) => {};
  // the current search body
  searchParams: {userID?: number; tournament?: Tournament};
  // the current state of matchmaking
  currentState: string;
  // the current app state
  appState: string;
  // the current polling status;
  polling: boolean;
  // the recent pings polled
  recentPings: number[];
  // the bars to display for connection status
  strengthBars: number;
  strongModalShown: boolean;
  elapsedTime: number;
  isCheckingForOpponent: boolean;
  isShowingEditStakesModal: boolean;
  subscriptions: Object[];
  blockModals: boolean;
  modalTest: boolean;
  tournamentName: string;
  ping: number;
  elapsedTimeHandlers: Object[];
  searchingTimerInterval?: number;
  gracePeriodTimeout?: number;
  pingPollTimeout?: number;
  opponentSearchTimeout?: number;
  checkMatchStartTimeout?: number;
  body?: {};
  updateHandler?: (error?: Object, response?: Object) => {};
  setID: number;
  estimatedWaitTime: number;
  tournamentTemplate: Tournament;
  isBotMatch: boolean;
  // If headlessSearch is true, the user will be enqueued automatically begin searching for an opponent found without
  // emitting the normal events indicating that a search is starting. This is used in the Sync FTUE for the first
  // match auto-confirm flow and the 2nd match after the notification prompt is dismissed. This is recommended for use only with
  // Sync FTUE where we must enqueue and search without changing screens.
  headlessSearch: boolean;
  // If headlessDequeue is true, the events fired when a user exceeds the max wait time or has a weak signal will not be fired
  // This is used in the Sync FTUE for the first match auto-confirm flow. This is recommended for use only in those flows
  headlessDequeue: boolean;
  // If headlessConfirm is true, the user will automatically confirm a found match without emitting the normal events
  // indicating that an opponent has been found. This will then funnel the user into the match. This is used in the Sync FTUE for the first
  // match auto-confirm flow and the 2nd match after the notification prompt is dismissed. This is recommended for use only with
  // bot matches
  headlessConfirm: boolean;
  opponentID: number;
  searchingTournaments: Array<Tournament>;

  constructor() {
    this._addListeners();
    this.currentState = MatchmakingConstants.NOT_ACTIVE;
    this.timeAdded = 0;
    this.appStateChangeListener = null;
    this._initializePingArray();
    this._checkIfCurrentUserWasMatchMaking();
  }

  setSearchingModalsEnabled(enable: boolean) {
    this.blockModals = !enable;
  }

  _checkIfCurrentUserWasMatchMaking() {
    if (SyncUtils.isMatchmakingActive()) {
      CurrentUserStore.getCurrentUserId().then((userId: string) => {
        const matchMakingUser = Prefs.get(CURRENTLY_MATCHMAKING_USER);
        const matchMakingTournament = Prefs.get(CURRENTLY_MATCHMAKING_TOURNAMENT);
        if (matchMakingUser == userId) {
          this._clearMatchMaking(matchMakingUser, matchMakingTournament);
        }
      });
    }
  }

  _initializePingArray() {
    this.recentPings = [];
    const PING_AVERAGE_COUNT = ClientPropertyConstants.getSyncPingAverageCount();
    for (let i = 0; i < PING_AVERAGE_COUNT; i++) {
      this.recentPings.push(0);
    }
  }

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];

    if (Platform.OS == 'ios') {
      this.appStateChangeListener = AppState.addEventListener('change', this._handleAppStateChange);
    }
    this.subscriptions.push(
      GlobalEventEmitter.addListener('onResume', this._showModalWhenNotBackgrounded.bind(this))
    );
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: unknown) => {
        if (listener) {
          listener.remove();
        }
      });
      this.subscriptions = [];
    }
    if (this.appStateChangeListener) {
      this.appStateChangeListener.remove();
    }
  }

  // MARK: matchmaking methods

  initiateMatchmakingForTournaments(
    userID: number,
    tournaments: Array<Tournament>,
    setID: number,
    isBotMatch: boolean,
    updateHandler?: (error?: Object, response?: Object) => {},
    hideLoadingIndicators: boolean
  ) {
    if (!tournaments || tournaments.length == 0) {
      this._initiateSearchError('no tournaments supplied');
      return;
    }

    let mainTournament = tournaments;
    if (!(tournaments instanceof Array)) {
      tournaments = [tournaments];
      this.tournamentName = tournaments[0].name;
    } else {
      mainTournament = _.find(
        StakesSelectorController.getAllSyncTournamentSets(),
        (template: SKZTournamentTemplateSet): boolean => template.id == setID
      );
      this.tournamentName = mainTournament.name;
    }
    if (!hideLoadingIndicators) {
      LoadingIndicator.show();
    }
    const tournamentIDs = _.map(_.map(tournaments, 'id'), (id: string): boolean => parseInt(id));

    const fetchDynamicDeviceInfo = SkillzModule.fetchDynamicDeviceInfo;
    fetchDynamicDeviceInfo((deviceInfo: Object, playerMetadata: Object) => {
      SyncUtils.setMatchmakingActive(true);
      Prefs.set({
        [CURRENTLY_MATCHMAKING_USER]: userID,
        [CURRENTLY_MATCHMAKING_TOURNAMENT]: mainTournament,
      });

      console.debug('SyncMatchMakingController: Starting to find match');
      const retryMode =
        Platform.OS == 'android' ? true : NativeModules.SKZAPIManager.OnError.failSilently;

      this.updateHandler = updateHandler;
      const body = {
        user_id: userID,
        tournament_template_ids: tournamentIDs,
        device: deviceInfo,
        is_bot_match: !!isBotMatch,
      };

      NetClient.postURL(MATCH_URL, body, retryMode, (error: unknown, payload: Object) => {
        LoadingIndicator.hide();
        if (!error && this.updateHandler) {
          this._prepareToFindMatch(payload, userID, mainTournament, isBotMatch);
        } else if (error) {
          this._initiateSearchError(JSON.stringify(error));
          if (this.updateHandler) {
            this.updateHandler(error, null);
          }
        }
      });
    });
  }

  _updateSearchingData(syncMatchParams: Object) {
    this.estimatedWaitTime = syncMatchParams.estimatedWaitTime;
    this.searchingTournaments = syncMatchParams.tournament;
    this.setID = syncMatchParams.setID;
    this.isBotMatch = syncMatchParams.isBotMatch;
    this.headlessSearch = syncMatchParams.headlessSearch;
    this.headlessDequeue = syncMatchParams.headlessDequeue;
    this.headlessConfirm = syncMatchParams.headlessConfirm;
  }

  _emitSyncSearching(showIndicator: boolean) {
    if (this.headlessSearch) {
      this.resumeCheckingForOpponent();
      return;
    }

    if (MatchJoinController.isHomescreenMounted()) {
      LoadingIndicator.hide();
      if (!this.headlessSearch) {
        GlobalEventEmitter.emit(MatchmakingConstants.START_SYNC_SEARCHING);
        if (this.isPooledLiquidity()) {
          NavigationManager.popToRoot();
        }
      }
    } else {
      if (!showIndicator) {
        LoadingIndicator.show();
      }
      setTimeout(() => {
        this._emitSyncSearching(true);
      }, 300); // eslint-disable-line no-magic-numbers
    }
  }

  // If headlessSearch is true, the the user will be enqueues and automatically begin polling for opponent found.
  // If headlessDequeue is true, the queue terminantion events will not fire (such as timeout exceeded, weak signal, etc)
  // If headlessConfirm is true, matches will automatically be confirmed and none of the matchmaking state
  // events will fire. This is intended for enqueueing, confirming, and entering a match without making
  // any visual updates for the user, such as on the first sync match in the sync FTUE or after the user
  // dismissed the notifications prompt during the Sync FTUE.
  startSearchingForSyncMatch(
    tournaments: Array<Tournament> | Tournament,
    shouldSkipRetry: boolean = false,
    setID: number | null,
    isBotMatch: boolean,
    headlessSearch: boolean,
    headlessDequeue: boolean,
    headlessConfirm: boolean,
    hideInitiateLoadingIndicators: boolean
  ) {
    const handleSyncMatch = (error: unknown, response: Object) => {
      if (error) {
        MatchJoinController.handleInitiateSyncMatchmakingError(error, tournaments, shouldSkipRetry);
        if (!headlessSearch) {
          GlobalEventEmitter.emit(MatchmakingConstants.NOT_ACTIVE);
        }
      } else if (response) {
        this.setDeviceKeepAwake(true);
        const responseData = {
          tournament: tournaments,
          estimatedWaitTime: response.estimatedWaitTime,
          setID: setID,
          headlessSearch: headlessSearch,
          headlessDequeue: headlessDequeue,
          headlessConfirm: headlessConfirm,
          isBotMatch: isBotMatch,
        };
        this._updateSearchingData(responseData);
        this._emitSyncSearching();
      }
    };

    CurrentUserStore.getCurrentUserId()
      .then((userID: Object) => {
        console.debug('SyncMatchMakingController: current user id: ' + userID);
        this.initiateMatchmakingForTournaments(
          userID,
          tournaments,
          setID,
          isBotMatch,
          handleSyncMatch,
          hideInitiateLoadingIndicators
        );
        console.debug('SyncMatchMakingController: called initiate w/ isBotMatch: ', isBotMatch);
      })
      .catch((error: unknown) => {
        console.debug(
          'SyncMatchMakingController: error getting current userID: ' + JSON.stringify(error)
        );
      });
  }

  isPooledLiquidity(): boolean {
    return this.setID != undefined;
  }

  _prepareToFindMatch(
    payload: Object,
    userID: number,
    mainTournament: Tournament,
    isBotMatch: boolean
  ) {
    this.currentState = MatchmakingConstants.SEARCHING_FOR_OPPONENT;
    const response = {
      state: this.currentState,
      estimatedWaitTime: payload.estimated_wait,
    };

    if (SyncUtils.isMatchmakingActive()) {
      this.searchParams = {
        userID: userID,
        tournament: mainTournament,
      };
    }

    if (!isBotMatch) {
      SkillzModule.setLocalNotificationListener(
        this._getSearchingNotificationTitle(),
        this._getSearchingNotificationBody()
      );
    }

    if (this.updateHandler) {
      this.updateHandler(null, response);
    }
    if (!this.polling) {
      this.startPollingPing();
    }
    this.startSearchingTimer();
  }

  _initiateSearchError(error: string) {
    console.debug('SyncMatchMakingController: initiate search ERROR: ' + error);
    SyncUtils.setMatchmakingActive(false);
  }

  isPollingPing(): boolean {
    return this.polling;
  }

  startPollingPing() {
    console.debug('SyncMatchMakingController: starting to poll TS for ping');
    this.initialTime = this.currentTime;
    this.strongModalShown = false;
    this.polling = true;
    this._startModalGracePeriod();
    this.pollPingStrength();
  }

  stopPollingPing() {
    this.polling = false;
    if (this.pingPollTimeout) {
      clearTimeout(this.pingPollTimeout);
      this.pingPollTimeout = null;
    }
    this.blockModals = true;
    this.modalTest = false;
  }

  _clearMatchMaking(userId: number, tournamentId: number, retryCount: number) {
    const deletionParams = {
      user_id: userId,
      tournament_id: null,
    };
    const retryMode =
      Platform.OS == 'android' ? true : NativeModules.SKZAPIManager.OnError.failSilently;
    NetClient.deleteURL(MATCH_URL, deletionParams, retryMode, (error: unknown, payload: Object) => {
      if (error && retryCount < MAX_RETRIES) {
        this._clearMatchMaking(userId, tournamentId, retryCount + 1);
      } else {
        Prefs.set({[IS_CURRENTLY_MATCHMAKING]: false});
        Prefs.set({[CURRENTLY_MATCHMAKING_USER]: null});
        Prefs.set({[CURRENTLY_MATCHMAKING_TOURNAMENT]: null});
      }
    });
  }

  onGameStarting() {
    this.blockModals = true;
  }

  onSyncGameStart() {
    this.stopPollingPing();
    this.resetAllControllerData();
  }

  terminateMatchmakingSearch(completion?: (error?: Object | null, response?: Object | null) => {}) {
    LoadingIndicator.show();
    this.setDeviceKeepAwake(false);
    const retryMode =
      Platform.OS == 'android' ? true : NativeModules.SKZAPIManager.OnError.failSilently;
    const deletionParams = {
      user_id: _.get(this.searchParams, 'userID'),
      tournament_id: null,
    };
    console.debug('SyncMatchMakingController: terminating search.');
    NetClient.deleteURL(
      MATCH_URL,
      deletionParams,
      retryMode,
      (error?: Object, payload?: Object) => {
        if (!error) {
          this.timeAdded = 0;

          SyncUtils.setMatchmakingActive(false);
          if (this.opponentSearchTimeout) {
            clearTimeout(this.opponentSearchTimeout);
            this.opponentSearchTimeout = undefined;
          }
          this.stopSearchingTimer();
          this.clearSearchingTimer();
          this.isCheckingForOpponent = false;

          LoadingIndicator.hide();
          if (completion) {
            this.currentState = MatchmakingConstants.NOT_ACTIVE;
            const response = {
              state: this.currentState,
            };
            completion(null, response);
          }
        } else {
          console.debug(
            'SyncMatchMakingController: terminating search ERROR: : ' + JSON.stringify(error)
          );
          LoadingIndicator.hide();
          completion?.(error, null);
        }
      }
    );
  }

  cancelMatchmaking(completion: unknown) {
    this.terminateMatchmakingSearch((error?: Object, response?: Object) => {
      if (this.isValidMatchmakingDeleteError(error)) {
        if (this.isPollingPing()) {
          this.stopPollingPing();
          this.rejectMatch();
          if (completion) {
            completion();
          }
        }
      }
    });
  }

  isValidMatchmakingDeleteError(error: Object): boolean {
    return (
      !error ||
      _.find(error.responseObject, (obj: Object): boolean => {
        return obj.name == JOIN_ERRORS.userNotFound;
      })
    );
  }

  _showModal(modal: React.ReactNode, modalProps: Object) {
    if (!this.modalShowing) {
      this.modalShowing = true;
      ModalNavigationController.showModal(modal, modalProps);
    }
  }

  _onDismissModal = () => {
    this._startModalGracePeriod();
    this.modalShowing = false;
    ModalNavigationController.hideModal();
  };

  /* this function exists so that a user doesn't get bombarded by these modals*/
  _startModalGracePeriod() {
    if (!this.blockModals) {
      this.blockModals = true;
      if (this.gracePeriodTimeout) {
        clearTimeout(this.gracePeriodTimeout);
      }

      this.gracePeriodTimeout = setTimeout(() => {
        this.blockModals = false;
      }, MODAL_GRACE_PERIOD);
    }
  }

  _handleUserKickedFromQueue() {
    this.rejectMatch();
    NavigationManager.popNavigator();
    if (!this.headlessSearch) {
      GlobalEventEmitter.emit(MatchmakingConstants.SYNC_WAIT_TIME_EXCEEDED);
    }
    ModalNavigationController.hideModal();
    this.modalShowing = false;
  }

  _showMatchUnavailableModal(isWifi: boolean) {
    const ALLOW_CELL_CONNECTION = ClientPropertyConstants.getIsSyncCellEnabled();
    if (!isWifi && !ALLOW_CELL_CONNECTION) {
      this.terminateMatchmakingSearch((error: unknown, response: unknown) => {
        // In this case, no matter what, we want to kick the user from the queue
        this._handleUserKickedFromQueue();
      });
    }
    const icon = isWifi ? 'ic_wifi_signal_2.png' : 'ic_cell_signal_1.png';
    const message =
      !isWifi && !ALLOW_CELL_CONNECTION
        ? _m(
            'This match is not currently supported over cellular. Please connect to wifi and retry.'
          )
        : _m(
            'Your connection strength is too weak to play this match. Please connect to wifi or find a stronger signal.'
          );
    const matchUnavailableModal = StaticWarningModal.createModal(
      icon,
      _m('MATCH UNAVAILABLE'),
      message,
      [_m('Cancel'), _m('Retry')],
      (index: number) => {
        if (!isWifi && !ALLOW_CELL_CONNECTION) {
          this._handleNoCellularConnectionCallback(index);
        } else {
          this._matchUnavailableCallback(index);
        }
      }
    );
    const matchUnavailableModalProps = {
      animationType: 'fade',
      transparent: true,
    };
    this._showModal(matchUnavailableModal, matchUnavailableModalProps);
  }

  _handleNoCellularConnectionCallback(index: number) {
    this.modalShowing = false;
    ModalNavigationController.hideModal();
    if (index == 1) {
      if (this.searchingTournaments) {
        MatchJoinController.displayJoinFlowForTournament(
          this.searchingTournaments,
          NavigationManager.getNavigator()
        );
      }
    }
  }

  _matchUnavailableCallback(index: number) {
    if (index == 0) {
      this.blockModals = true;
      this.terminateMatchmakingSearch((error: unknown, response: unknown) => {
        // In this case, no matter what, we want to kick the user from the queue
        this._handleUserKickedFromQueue();
      });
    } else {
      this._onDismissModal();
    }
  }

  _checkShowModal(pingAverage: number, isWifi: boolean) {
    const ALLOW_CELL_CONNECTION = ClientPropertyConstants.getIsSyncCellEnabled();
    const MIN_PING_STRENGTH = ClientPropertyConstants.getSyncMinPingStrength();
    const STRONG_PING_STRENGTH = ClientPropertyConstants.getSyncStrongPingStrength();

    const isTooWeak = pingAverage > MIN_PING_STRENGTH;
    if (isTooWeak || (!isWifi && !ALLOW_CELL_CONNECTION)) {
      this._showMatchUnavailableModal(isWifi);
    } else if (!this.strongModalShown && pingAverage > STRONG_PING_STRENGTH && !this.blockModals) {
      this.strongModalShown = true;
      setTimeout(() => {
        this._showStrengthDroppedModal();
      }, SKZConstants.MS_PER_SEC);
    }
  }

  _showStrengthDroppedModal() {
    const strengthDroppedModal = StaticWarningModal.createModal(
      'ic_warning_exclamation.png',
      _m('YOUR CONNECTION STRENGTH HAS DROPPED'),
      _m('Continue searching for an opponent?'),
      [_m('Cancel'), _m('Continue')],
      (index: number) => {
        this._showStrengthDroppedModalCallback(index);
      }
    );
    const strengthDroppedModalProps = {
      animationType: 'fade',
      transparent: true,
    };
    this._showModal(strengthDroppedModal, strengthDroppedModalProps);
  }

  _showStrengthDroppedModalCallback(index: number) {
    this._onDismissModal();
    this.blockModals = true;

    if (index === 0) {
      this.terminateMatchmakingSearch((error: unknown, response: unknown) => {
        if (this.isValidMatchmakingDeleteError(error)) {
          this.rejectMatch();
          NavigationManager.popNavigator();
          if (!this.headlessDequeue) {
            GlobalEventEmitter.emit(MatchmakingConstants.SYNC_WAIT_TIME_EXCEEDED);
          }
          ModalNavigationController.hideModal();
        }
      });
    }
  }

  pollPingStrength() {
    NetworkConnectivityClient._getPingOnce((ping: Object) => {
      let newPing = ping.ping;
      if (this.modalTest) {
        newPing = this.ping + 10; // eslint-disable-line no-magic-numbers
      }
      this.recentPings.shift();
      this.recentPings.push(newPing);
      if (ping.isWifi) {
        this.strengthBars = NetworkConnectivityClient._getWifiSignalStrength(this.pingAverage);
      } else {
        this.strengthBars = NetworkConnectivityClient._getCellSignalStrength(this.pingAverage);
      }
      if (this.polling) {
        this._checkShowModal(this.pingAverage, ping.isWifi);
      }
      const PING_POLL_INTERVAL = ClientPropertyConstants.getSyncPingCheckInterval();
      this.pingPollTimeout = setTimeout(() => {
        if (!this.polling) {
          return;
        }
        this.ping = newPing;
        this.pollPingStrength();
      }, PING_POLL_INTERVAL);
    });
  }

  resumeCheckingForOpponent() {
    this._extendSearchingTime();
    console.debug('SyncMatchMakingController: resume checking for opponent');
    if (!this.isCheckingForOpponent) {
      console.debug('SyncMatchMakingController:not checking yet, continue.');
      if (this.opponentSearchTimeout) {
        clearTimeout(this.opponentSearchTimeout);
      }

      if (Platform.OS == 'android' && !this.isBotMatch) {
        SkillzModule.setLocalNotificationListener(
          this._getSearchingNotificationTitle(),
          this._getSearchingNotificationBody()
        );
      }

      Prefs.set({[IS_CURRENTLY_MATCHMAKING]: true});
      this.isCheckingForOpponent = true;
      this.currentState = MatchmakingConstants.SEARCHING_FOR_OPPONENT;
      this.startSearchingTimer();
      this._checkForOpponentFound();
    }
  }

  _completeTournamentObjectFromPayload(payload: Object) {
    this.matchmakerMatchID = payload.matchmaker_match_id;
    /**
     * TS doesn't send full tournament_template JSONs on the GET call, so we have to fill
     * in where there are holes to avoid crashes on Android. Easier/cleaner to mutate a new
     * object, and doesn't create issues with assignment
     */
    this.tournament = new Tournament(payload.tournament_template);
    this.tournament.type = payload.tournament_template['type'] || 'LIMITED_ENTRY';
    this.tournament.id = `${payload.tournament_template['id']}`;
    this.tournament.ticketz_prize = payload.tournament_template['ticketz_prize'] || 0;
    this.tournament.rules = payload.tournament_template['rules'] || [];
    this.tournament.synchronous = true;

    _.forEach(payload.tournament_template, (value: unknown, key: unknown) => {
      if (payload.tournament_template.hasOwnProperty(key)) {
        if (key == 'zfee') {
          this.tournament.z_fee = payload.tournament_template[key];
        } else {
          this.tournament.key = payload.tournament_template[key];
        }
      }
    });
  }

  _checkForOpponentFound() {
    const OPPONENT_SEARCH_INTERVAL = ClientPropertyConstants.getSyncVersusSearchingTime();
    console.debug('SyncMatchMakingController: check opponent found: ' + OPPONENT_SEARCH_INTERVAL);
    const retryMode =
      Platform.OS == 'android' ? true : NativeModules.SKZAPIManager.OnError.failSilently;

    NetClient.getURL(MATCH_URL, {}, retryMode)
      .then((payload: Object) => {
        if (payload.matchmaker_match_id && payload.opponent_user_id) {
          console.debug('SyncMatchMakingController: opponent found');
          this._handleOpponentFound(payload);
        } else {
          // No match found yet, keep polling
          if (this.isCheckingForOpponent) {
            this.opponentSearchTimeout = setTimeout(() => {
              this._checkForOpponentFound();
            }, OPPONENT_SEARCH_INTERVAL);
          }
        }
      })
      .catch((error: Object) => {
        // Connection issue, keep polling
        if (this.isCheckingForOpponent) {
          // iOS uses error.code, Android uses error.status
          const MAX_WAIT_TIME = ClientPropertyConstants.getSyncMaxWaitTime();
          if (this.timeSearching * MILLIS_PER_SECOND < MAX_WAIT_TIME) {
            if (error.code == NOT_FOUND || error.status == NOT_FOUND) {
              // User was kicked from the queue by TS after missing heartbeat. Should re-enqueue
              MatchSelectController.handleJoinTappedCell(
                this.searchingTournaments,
                NavigationManager.getNavigator(),
                null,
                this.setID
              );
            }
            this.opponentSearchTimeout = setTimeout(() => {
              this._checkForOpponentFound();
            }, OPPONENT_SEARCH_INTERVAL);
          }
        }
      });
  }

  _handleOpponentFound(payload: Object) {
    this._completeTournamentObjectFromPayload(payload);
    this.timeAdded += this.timeSearching;
    this.isCheckingForOpponent = false;
    this.currentState = MatchmakingConstants.OPPONENT_FOUND;
    Prefs.set({[IS_CURRENTLY_MATCHMAKING]: false});
    this.stopSearchingTimer();
    ModalNavigationController.hideModal();

    if (this.headlessConfirm) {
      if (Platform.OS == 'android') {
        SkillzModule.setLocalNotificationListener(null, null);
      }

      this.confirmMatch(
        this.getTournament(),
        this.getMatchMakerMatchID(),
        this._headlessOpponentFoundCompletion
      );
      return;
    }

    GlobalEventEmitter.emit(MatchmakingConstants.OPPONENT_FOUND);

    if (Platform.OS == 'android') {
      SkillzModule.setLocalNotificationListener(
        this._getOpponentFoundNotificationTitle(),
        this._getOpponentFoundNotificationBody()
      );
      SkillzModule.setLocalNotificationListener(null, null);
    } else if (Platform.OS == 'ios' && this.appState != 'active') {
      SkillzModule.sendLocalNotification(
        this._getOpponentFoundNotificationTitle(),
        this._getOpponentFoundNotificationBody()
      );
    }
  }

  confirmMatch(
    tournament: Tournament,
    matchmakerMatchID: string,
    completion: (error?: Object, response?: Object) => {}
  ) {
    LoadingIndicator.show();

    this.isCheckingForOpponent = false;
    const fetchDynamicDeviceInfo = SkillzModule.fetchDynamicDeviceInfo;
    fetchDynamicDeviceInfo((deviceInfo: Object, playerMetadata: Object) => {
      const url = MATCH_URL + '/' + matchmakerMatchID;
      const body = {
        tournament_id: tournament.id,
        device: deviceInfo,
      };
      const retryMode =
        Platform.OS == 'android' ? true : NativeModules.SKZAPIManager.OnError.failSilently;
      NetClient.postURL(url, body, retryMode, (err: unknown, payload: Object) => {
        console.debug(
          'SyncMatchMakingController: confirming match: err: ' +
            JSON.stringify(err) +
            ' | payload: ' +
            JSON.stringify(payload)
        );
        LoadingIndicator.hide();
        if (err) {
          const errorName = _.get(err, 'responseObject[0].name', 'Network failure');
          const msg = _.get(err, 'responseObject[0].message', null);
          if (errorName == JOIN_ERRORS.matchNotFound) {
            this.currentState = MatchmakingConstants.OPPONENT_CANCELLED;
            const response = {
              state: this.currentState,
            };
            completion(null, response);
          } else {
            const errorCompletion = () => {
              completion(err, null);
            };
            this._handleConfirmationError(errorName, msg, tournament, errorCompletion);
          }
        } else {
          this.stopPollingPing();
          this.currentState = MatchmakingConstants.WAITING_FOR_OPPONENT;
          const response = {
            state: this.currentState,
          };
          completion(null, response);
          this._pollOpponentConfirmed(matchmakerMatchID, tournament, completion);
        }
      });
    });
  }

  _handleConfirmationError(
    errorName: string,
    msg: string,
    tournament: Tournament,
    errorCompletion: unknown
  ) {
    console.debug(
      'SyncMatchMakingController _handleConfirmationError: error attempting to confirm: ' +
        errorName
    );
    if (errorName == JOIN_ERRORS.insufficientFunds) {
      const isCash = tournament.cash_fee > 0;
      this._handleInsufficientFundsError(isCash, errorCompletion);
    } else if (errorName == JOIN_ERRORS.noServerAvailable) {
      this._displayErrorModalWithMessage(errorName, msg, errorCompletion);
    } else {
      this.displayDefaultErrorModal(errorCompletion);
    }
  }

  _handleInsufficientFundsError(isCash: boolean, completion: unknown) {
    CheckoutFlowModalController.showAppropriateOutOfFundsModal(isCash, completion);
  }

  rejectMatch() {
    // No API call for rejecting, simply need to reset the data
    this.resetAllControllerData();
  }

  _pollOpponentConfirmed(
    matchmakerMatchID: string,
    tournament: Tournament,
    completion: (error?: Object, response?: Object) => {}
  ) {
    const url = MATCH_URL + '/' + matchmakerMatchID;
    const retryMode =
      Platform.OS == 'android' ? true : NativeModules.SKZAPIManager.OnError.failSilently;
    console.debug('SyncMatchMakingController: poll opponent confirmed');
    NetClient.getURL(url, {}, retryMode)
      .then((payload: Object) => {
        if (!_.isEmpty(payload)) {
          this._clearCheckMatchStartPoll();
          if (payload.connect_token) {
            this.connect_token = payload.connect_token;
            payload.matchmaker_match_id = this.matchmakerMatchID;
            console.debug(
              'SyncMatchMakingController: _pollOpponentConfirmed: Received Connect Token in response && added matchmaking_match_id: ' +
                JSON.stringify(payload) +
                '\nStarting Game...'
            );
            this.currentState = MatchmakingConstants.STARTING_GAME;
            this.timeAdded = 0;
            const response = {
              state: this.currentState,
              match: new Match(payload),
            };
            // game was found
            completion(null, response);
          } else {
            console.debug('SyncMatchMakingController: Opponent Cancelled! ');
            this.currentState = MatchmakingConstants.OPPONENT_CANCELLED;
            const response = {
              state: this.currentState,
              payload: payload,
            };
            // no game could be found,  handle the 'error'

            completion(null, response);
          }
        } else {
          const CHECK_MATCH_CONFIRMED_INTERVAL =
            ClientPropertyConstants.getSyncVersusSearchingTime();
          this._clearCheckMatchStartPoll();
          this.checkMatchStartTimeout = setTimeout(() => {
            this._pollOpponentConfirmed(matchmakerMatchID, tournament, completion);
          }, CHECK_MATCH_CONFIRMED_INTERVAL);
        }
      })
      .catch((error: Object) => {
        this._clearCheckMatchStartPoll();
        const errorName = _.get(error, 'responseObject[0].name', 'Network failure');
        const msg = _.get(error, 'responseObject[0].message', null);
        const errorCompletion = () => {
          completion(error, null);
        };

        if (error.code == NOT_FOUND) {
          this.displayOpponentErrorModal(tournament);
        } else {
          this._handleConfirmationError(errorName, msg, tournament, errorCompletion);
        }
      });
  }

  _headlessOpponentFoundCompletion = async (error: Object, response: Object) => {
    if (response?.state == MatchmakingConstants.OPPONENT_CANCELLED) {
      this.resetControllerOpponentFoundData();
    } else if (response?.state == MatchmakingConstants.STARTING_GAME) {
      const match = new Match(response.match);
      const userId = await CurrentUserStore.getCurrentUserId();
      MatchJoinController.prepareForControlPassingPostSuccessfulJoin(match, userId, () => {
        MatchJoinController.passControlToPublisherForJoin(this.getTournament(), match);
      });
    }
  };

  _clearCheckMatchStartPoll() {
    if (this.checkMatchStartTimeout) {
      clearTimeout(this.checkMatchStartTimeout);
      this.checkMatchStartTimeout = undefined;
    }
  }

  // MARK: time keeping

  addElapsedTimeListener(handler: (elapsedTime: number) => {}): (elapsedTime: number) => {} {
    if (!this.elapsedTimeHandlers) {
      this.elapsedTimeHandlers = [];
    }
    this.elapsedTimeHandlers.push(handler);
    return handler;
  }

  removeElapsedTimeListener(handler: null | ((elapsedTime: number) => {})) {
    if (this.elapsedTimeHandlers && handler) {
      _.pull(this.elapsedTimeHandlers, handler);
    }
  }

  _isSyncFTUE(): boolean {
    return (
      SyncFTUEController.canShowSecondSyncFTUEVersus() ||
      SyncFTUEController.canShowSyncFTUENthGameVersus()
    );
  }

  _shouldEnqueueAgainstBotOnTimeout(): boolean {
    return ClientPropertyConstants.getSyncFTUECheckMaxWaitTime() && this._isSyncFTUE();
  }

  _getSearchTimeoutMillis(): number {
    return this._shouldEnqueueAgainstBotOnTimeout()
      ? ClientPropertyConstants.getSyncFTUEMaxWaitTime()
      : ClientPropertyConstants.getSyncMaxWaitTime();
  }

  startSearchingTimer() {
    const MAX_WAIT_TIME = this._getSearchTimeoutMillis();
    if (this.timeSearching * MILLIS_PER_SECOND >= MAX_WAIT_TIME) {
      this._handleMaxWaitExceeded();
      return;
    }
    this.stopSearchingTimer();

    this.searchingTimerInterval = setInterval(() => {
      this._handleElapsedTime();
      if (this.timeSearching * MILLIS_PER_SECOND >= MAX_WAIT_TIME) {
        this._handleMaxWaitExceeded();
      }
    }, MILLIS_PER_SECOND);
  }

  _handleMaxWaitExceeded() {
    this.terminateMatchmakingSearch(this._handleMaxWaitCallback);
  }

  _handleMaxWaitCallback = (error: Object, response: Object) => {
    const searchingTournaments = this._shouldEnqueueAgainstBotOnTimeout()
      ? this.searchingTournaments
      : null;
    if (this.isValidMatchmakingDeleteError(error)) {
      this.rejectMatch();
      this.stopPollingPing();
      this.resetAllControllerData();
      this.stopSearchingTimer();
      this.clearSearchingTimer();

      //Shut off notification listener on Android since checking for background is done on the native side.
      if (Platform.OS == 'android') {
        SkillzModule.setLocalNotificationListener(null, null);
      }

      if (this._shouldEnqueueAgainstBotOnTimeout()) {
        MatchSelectController.handleJoinTappedCell(
          searchingTournaments,
          NavigationManager.getNavigator(),
          null,
          this.setID,
          true
        );
      } else {
        this._displayTimeoutModal();
      }
    }
  };

  _displayTimeoutModal() {
    const title = _m('Sorry!');
    const message = _m('This is taking longer than expected. Please try again later.');
    const buttonTitles = [_m('OK')];
    const completion = () => {
      this.terminateMatchmakingSearch((error?: Object, response?: Object) => {
        if (this.isValidMatchmakingDeleteError(error)) {
          GlobalEventEmitter.emit('switchToTab', 'PLAY');
          this.rejectMatch();
          this.stopPollingPing();
          this.resetAllControllerData();
          this.stopSearchingTimer();
          this.clearSearchingTimer();
          ModalNavigationController.hideModal();
          if (!this.headlessDequeue) {
            GlobalEventEmitter.emit(MatchmakingConstants.SYNC_WAIT_TIME_EXCEEDED);
          }
          this.blockModals = false;
        } else {
          ModalNavigationController.hideModal();
        }
      });
    };
    ModalNavigationController.hideModal();
    const modal = this._createModal(
      null,
      title,
      'long_wait_header',
      message,
      'long_wait_subtext',
      buttonTitles,
      ['OK'],
      completion
    );

    const modalProps = {
      animationType: 'fade',
      transparent: true,
    };
    if (Platform.OS === 'android') {
      this._handleMaxWaitAndroid(modal, modalProps);
    } else {
      ModalNavigationController.showModal(modal, modalProps);
    }
    this.blockModals = true;
  }

  _handleMaxWaitAndroid(modal: React.ReactNode, modalProps: Object) {
    SkillzModule.isActivityBackgrounded((isBackgrounded: boolean) => {
      if (isBackgrounded) {
        this.showModalWhenOpen = true;
        this.modalToShowWhenOpen = modal;
        this.modalPropsForDelayedModal = modalProps;
      } else {
        this.showModalWhenOpen = false;
        ModalNavigationController.showModal(modal, modalProps);
      }
    });
  }

  _showModalWhenNotBackgrounded() {
    if (this.showModalWhenOpen) {
      this.showModalWhenOpen = false;
      ModalNavigationController.showModal(this.modalToShowWhenOpen, this.modalPropsForDelayedModal);
    }
  }

  _handleElapsedTime() {
    console.debug(`SyncMatchMakingController: Handling elapsed time: ${this.timeSearching}`);
    if (this.elapsedTimeHandlers) {
      this.elapsedTimeHandlers.forEach((handler: (elapsedTime: number) => {}) => {
        if (handler) {
          handler(this.timeAdded + this.timeSearching);
        }
      });
    }
  }

  stopSearchingTimer() {
    if (this.searchingTimerInterval) {
      clearInterval(this.searchingTimerInterval);
      this.searchingTimerInterval = null;
    }
  }

  clearSearchingTimer() {
    this.initialTime = 0;
  }

  userAlreadyInSyncMatch(): boolean {
    if (this.currentState !== MatchmakingConstants.NOT_ACTIVE) {
      return true;
    }
    return false;
  }

  displayTournamentPendingModal() {
    AWSManager.trackEvent('ACTION_RESTRICTED_MODAL');
    const dismissModalCallback = () => {
      ModalNavigationController.hideModal();
    };
    const tournamentPendingModal = <TournamentPendingModal callback={dismissModalCallback} />;
    const tournamentPendingModalProps = {
      animationType: 'fade',
      transparent: true,
    };
    ModalNavigationController.showModal(tournamentPendingModal, tournamentPendingModalProps);
  }

  displayOpponentErrorModal(tournament: Tournament) {
    const header = _m('Your opponent has cancelled.');
    const message = _m('We are searching for a new opponent of similar skill level.');
    const buttonTitleArray = [_m('OK')];

    const callback = () => {
      if (this.modalTimeout) {
        clearTimeout(this.modalTimeout);
      }
      ModalNavigationController.hideModal();
      if (tournament !== null) {
        this._handleRequeueing(tournament);
      }
    };

    const warningModal = this._createModal(
      null,
      header,
      'opponent_cancelled_header',
      message,
      'opponent_cancelled_subtext',
      buttonTitleArray,
      ['OK'],
      callback
    );

    const warningModalProps = {
      animationType: 'fade',
      transparent: true,
    };

    ModalNavigationController.showModal(warningModal, warningModalProps);
    const MODAL_DURATION = ClientPropertyConstants.getSyncVersusOpponentModalDuration();
    this.modalTimeout = setTimeout(() => {
      ModalNavigationController.hideModal();
      this._handleRequeueing(tournament);
    }, MODAL_DURATION);
  }

  _handleRequeueing(tournament: Tournament) {
    NavigationManager.pop();

    if (!this.headless) {
      GlobalEventEmitter.emit(MatchmakingConstants.SEARCHING_FOR_OPPONENT);
    }

    setTimeout(() => {
      this.startPollingPing();
    }, SKZConstants.MS_PER_SEC);
    MatchJoinController.displayJoinFlowForTournament(
      tournament,
      NavigationManager.getNavigator(),
      null,
      false,
      tournament.set_id
    );
  }

  displayDefaultErrorModal(completion: unknown) {
    this._displayErrorModalWithMessage(
      _m(`It doesn't look like that worked. Please try again later.`),
      completion
    );
  }

  _displayErrorModalWithMessage(message: string, completion: () => {}) {
    const header = _m('Sorry!');
    const buttonTitleArray = [_m('OK')];

    const callback = () => {
      if (completion) {
        completion();
      }
      ModalNavigationController.hideModal();
    };

    const warningModal = StaticWarningModal.createModal(
      null,
      header,
      message,
      buttonTitleArray,
      callback,
      callback
    );

    const warningModalProps = {
      animationType: 'fade',
      transparent: true,
    };

    ModalNavigationController.showModal(warningModal, warningModalProps);
  }

  displayMatchLeaveError(completion: unknown) {
    const title = _m('Sorry!');
    const message = _m('Unable to leave the queue. Please try again later.');
    const buttonTitles = [_m('CANCEL'), _m('TRY AGAIN')];
    const callback = (index: number) => {
      if (index == 0) {
        ModalNavigationController.hideModal();
      } else {
        ModalNavigationController.hideModal();
        if (completion) {
          completion();
        }
      }
    };

    StaticWarningModal.createModal(null, title, message, buttonTitles, callback);
  }

  _createModal(
    icon: string,
    title: string,
    titleTestID: string,
    message: string,
    messageTestID: string,
    buttonTitleArray: Array<string>,
    buttonTestIDArray: Array<string>,
    callback: () => {}
  ): React.ReactNode {
    const props = {
      title,
      titleTestID,
      titleAccessibilityLabel: title,
      message,
      messageTestID,
      messageAccessibilityLabel: message,
      buttonTitleArray,
      buttonTestIDArray,
      callback,
    };
    if (icon) {
      props.icon = icon;
    }

    return <StaticWarningModal {...props} />;
  }

  // MARK: Notifications

  _handleAppStateChange = (nextAppState: string) => {
    this.appState = nextAppState;
    const MAX_WAIT_TIME = ClientPropertyConstants.getSyncMaxWaitTime();
    if (
      this.appState == 'background' &&
      this.currentState == MatchmakingConstants.SEARCHING_FOR_OPPONENT &&
      this.timeSearching * MILLIS_PER_SECOND < MAX_WAIT_TIME
    ) {
      SkillzModule.sendLocalNotification(
        this._getSearchingNotificationTitle(),
        this._getSearchingNotificationBody()
      );
    } else if (
      this.appState == 'active' &&
      this.currentState == MatchmakingConstants.SEARCHING_FOR_OPPONENT
    ) {
      if (!this.headless) {
        GlobalEventEmitter.emit('switchToTab', 'PLAY');
      }
    }
  };

  _getSearchingNotificationTitle(): string {
    return ClientPropertyConstants.getSyncNotifBackgroundTitle();
  }

  _getOpponentFoundNotificationTitle(): string {
    return ClientPropertyConstants.getSyncNotifOpponentFoundTitle();
  }

  _getSearchingNotificationBody(): string {
    let notifBody = ClientPropertyConstants.getSyncNotifBackgroundBody();
    notifBody = notifBody.replace('{{{match_name}}}', this.tournamentName);
    return notifBody;
  }

  _getOpponentFoundNotificationBody(): string {
    let notifBody = ClientPropertyConstants.getSyncNotifOpponentFoundBody();
    notifBody = notifBody.replace('{{{match_name}}}', this.tournamentName);
    const handshakeSeconds =
      ClientPropertyConstants.getSyncVersusHandshake() / SKZConstants.MS_PER_SEC;
    notifBody = notifBody.replace('{{{handshake_length}}}', String(handshakeSeconds));
    return notifBody;
  }

  getMatchMakerMatchID(): string {
    return this.matchmakerMatchID;
  }

  getTournament(): Tournament {
    return this.tournament;
  }

  getEstimatedWaitTime(): number {
    return this.estimatedWaitTime;
  }

  getSetID(): number {
    return this.setID;
  }

  getSearchingTournaments(): Array<Tournament> {
    return this.searchingTournaments;
  }

  getCurrentState(): string {
    return this.currentState;
  }

  getEditStakesModalStatus(): boolean {
    return this.isShowingEditStakesModal;
  }

  setEditStakesModalStatus(showing: boolean) {
    this.isShowingEditStakesModal = showing;
  }

  isInQueue(): boolean {
    return this.currentState !== MatchmakingConstants.NOT_ACTIVE;
  }

  // MARK: Cleanup processes

  resetSetID() {
    this.setID = null;
  }

  resetAllControllerData() {
    this.resetControllerOpponentFoundData();
    this.stopSearchingTimer();
    this.clearSearchingTimer();
    this.searchingTournaments = null;
    this.timeAdded = 0;
    this.timeSearching = 0;
    this.searchParams = {};
    this.currentState = MatchmakingConstants.NOT_ACTIVE;
    this.updateHandler = null;
    this.estimatedWaitTime = null;
    this.isBotMatch = false;
    this.headless = false;
  }

  resetControllerOpponentFoundData() {
    this.opponentID = null;
    this.tournamentTemplate = null;
  }

  _extendSearchingTime() {
    this.initialTime = this.currentTime;
  }

  get pingAverage(): number {
    let sum = 0;
    const PING_AVERAGE_COUNT = ClientPropertyConstants.getSyncPingAverageCount();
    for (let index = 0; index < PING_AVERAGE_COUNT; index++) {
      sum += this.recentPings[index];
    }
    return sum / PING_AVERAGE_COUNT;
  }

  get currentTime(): number {
    return SyncUtils.getCurrentTimeInSecs();
  }

  get timeSearching(): number {
    return this.currentTime - this.initialTime;
  }

  setDeviceKeepAwake(keepAwake: boolean) {
    // Client prop controls if we want matchmaking to affect device idle sleep behavior while in queue
    if (!ClientPropertyConstants.getSyncMatchMakingAllowSleep()) {
      console.debug(
        'SyncMatchMakingController: updating device idle sleep behavior, keepAwake',
        keepAwake
      );
      SkillzModule.setDeviceKeepAwake?.(keepAwake);
    }
  }

  showProgressionLeaveQueueModal(progressionName: string, onConfirm: () => {}) {
    // Used to show a modal when going to Progression, as we must leave the queue first
    const modal = StaticWarningModal.createModal(
      null,
      _m('View {0}?', progressionName),
      _m('Viewing {0} will stop your search for a Real-Time Match.', progressionName),
      [_m('Cancel'), _m('OK')],
      (buttonIndex: number) => {
        if (buttonIndex == 0) {
          // Cancels
          ModalNavigationController.hideModal();
        } else if (buttonIndex == 1) {
          // Confirms
          LoadingIndicator.show();

          // Android's react bridge is handled differently. If we try to return to the SDK
          // while we're in the matchmaking state like this, we will get stuck,
          // and if the modal is still trying to show, we will crash.
          // So on Android we need to do some preparation to make this work.
          if (Platform.OS === 'android') {
            GlobalEventEmitter.emit('switchToTab', constants.PLAY);
          }
          this.cancelMatchmaking(() => {
            GlobalEventEmitter.emit(MatchmakingConstants.NOT_ACTIVE);
            ModalNavigationController.hideModal();
            LoadingIndicator.hide();
            onConfirm?.();
          });
        }
      }
    );

    const modalProps = {
      animationType: 'fade',
      transparent: true,
    };
    ModalNavigationController.showModal(modal, modalProps);
  }
}
const SyncMatchMakingControllerInstance = new SyncMatchMakingController();
SyncMatchMakingControllerInstance.IS_CURRENTLY_MATCHMAKING = IS_CURRENTLY_MATCHMAKING;
export default SyncMatchMakingControllerInstance;
